import moment from "moment-timezone";

import { HITL_TASK_TYPES } from "../../constant/agenticPlatform";
import { DATE_FORMAT_1, dateFormatter } from "../../utils/date";

/**
 * Shared row formatters for the Tasks hub sections (screenshots + the two
 * HITL feeds). Kept in one place so the screenshots and HITL tables render
 * patient labels + ages identically.
 */

/**
 * Display label for a patient row: the PHI-hydrated `patientName` followed by
 * the TailorMed patient id in brackets ("First Last (1234)"). Falls back to
 * `Patient #${id}` for non-PHI callers or rows missing PHI data (no redundant
 * trailing id in that case).
 */
export function patientLabel(row) {
  const id = row?.patientId ?? "?";
  if (row?.patientName) return `${row.patientName} (${id})`;
  return `Patient #${id}`;
}

/** Relative "pending since" age, e.g. "3 hours ago". Empty for missing dates. */
export function formatRelativeTime(iso) {
  if (!iso) return "";
  return moment(iso).fromNow();
}

/**
 * Human date for step / summary payload values ("Oct 6, 2026"). Payload dates
 * arrive as raw ISO timestamps ("2026-10-06T00:00:00.000Z"), which must never
 * reach the navigator's screen; anything unparseable passes through untouched
 * so a legacy free-text value still renders.
 */
export function formatShortDate(value) {
  if (!value) return value;
  // The two guards stay local: the shared `dateFormatter` answers null for a
  // falsy date and has no opinion on unparseable input, whereas a payload value
  // that isn't a date must pass through untouched. Formatting itself delegates,
  // so there is one moment().format() in the app, not two.
  return moment(value).isValid() ? dateFormatter(value, DATE_FORMAT_1) : value;
}

/**
 * Total tasks waiting on a navigator, across every HITL queue. Defined once so
 * adding a queue type is a change here rather than in each surface showing a
 * total. A key missing from an older platform payload mid-deploy contributes 0
 * rather than turning the whole sum into NaN.
 */
export function sumWaitingTasks(waiting) {
  const counts = waiting ?? {};
  return (
    (counts.consentApproval ?? 0) + (counts.emrFill ?? 0) + (counts.screenshotsUpload ?? 0) + (counts.eeReview ?? 0)
  );
}

/**
 * The context cell for a HITL row — the mockup's "Reason" (Manual EMR fill) /
 * "State" (Consent) column, derived from the open `human_review` step's
 * `review`:
 *   - emr_fill  → the gathering `exitMode` ("Wrong patient uploaded", …)
 *   - consent   → "Contact info missing" when contact fields are absent,
 *                 otherwise the humanized consent outcome ("Eligible to send")
 * Copy comes from the `agentic_tasks.emr_reason.*` / `agentic_tasks.consent_state.*`
 * i18n keys (en.js owns the labels), keyed by the enum value.
 */
export function hitlContextLabel(item, t) {
  const review = item?.review || {};
  const payload = review.payload || {};

  if (item?.hitlTaskType === HITL_TASK_TYPES.EMR_FILL) {
    const mode = payload.exitMode;
    if (!mode) return "—";
    return t(`agentic_tasks.emr_reason.${mode}`);
  }

  if (item?.hitlTaskType === HITL_TASK_TYPES.CONSENT_DECIDE) {
    if (Array.isArray(payload.missingContactFields) && payload.missingContactFields.length > 0) {
      return t("agentic_tasks.consent_state.contact_missing");
    }
    const outcome = review.outcome || payload.consentOutcome;
    return outcome ? t(`agentic_tasks.consent_state.${outcome}`) : "—";
  }

  // `ee_review`: the enrollment's identity IS its program + drug — one patient
  // can hold two enrollment tasks, and only this tells the rows apart. The names
  // are written onto the pause step when the agent creates the task (non-PHI, a
  // snapshot of what it was enrolling), so they ride in on the feed itself.
  if (item?.hitlTaskType === HITL_TASK_TYPES.EE_REVIEW) {
    const parts = [payload.programName, payload.drugName].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : "—";
  }

  return "—";
}
